import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  fetchTrackingLocationsApi, 
  createTrackingLocationApi, 
  updateTrackingLocationApi, 
  deleteTrackingLocationApi,
  fetchUsersApi
} from '../../services/api';

export default function SeguimientoUbicaciones() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ 
    userId: '', 
    latitude: '', 
    longitude: '', 
    locationDate: '', 
    accuracy: 0 
  });

  // Cargar datos iniciales
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setUsersLoading(true);
        const usersList = await fetchUsersApi({ token });
        if (!active) return;
        setUsers(usersList);
        setUsersLoading(false);
      } catch (err) {
        if (!active) return;
        setUsersLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!active) return;
        const list = await fetchTrackingLocationsApi({ token });
        if (!active) return;
        setRows(list);
      } catch (e) {
        /* noop */
      }
    })();
    return () => { active = false; };
  }, [token]);

  const reload = async () => {
    const list = await fetchTrackingLocationsApi({ token });
    setRows(list);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ 
      userId: '', 
      latitude: '', 
      longitude: '', 
      locationDate: '', 
      accuracy: 0 
    });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({ 
      userId: row.userId, 
      latitude: row.latitude || '', 
      longitude: row.longitude || '', 
      locationDate: row.locationDate || '', 
      accuracy: row.accuracy || 0 
    });
    setShowForm(true);
  };

  const [toasts, setToasts] = useState([]);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const pushToast = (type, msg) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, msg }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== t.id));
    }, 4000);
  };

  const removeRow = async (id) => {
    try {
      await deleteTrackingLocationApi({ id, token });
      await reload();
      pushToast('success', 'Eliminado correctamente');
    } catch (err) {
      pushToast('danger', err.message || 'No se pudo eliminar');
    }
  };

  const saveForm = async (e) => {
    e.preventDefault();
    const { userId, latitude, longitude, locationDate, accuracy } = form;
    if (!userId || !latitude || !longitude || !locationDate) return;
    
    try {
      let res;
      if (editingId == null) {
        res = await createTrackingLocationApi({
          userId,
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          locationDate,
          accuracy: parseFloat(accuracy),
          token,
        });
        if (res?.isSuccess) {
          await reload();
          pushToast('success', 'Seguimiento de Ubicación creado correctamente');
        } else {
          throw new Error(res?.message || 'No se pudo registrar');
        }
      } else {
        // Actualmente no hay endpoint para actualizar seguimiento de ubicaciones
        pushToast('warning', 'La actualización de seguimiento de ubicaciones no está disponible actualmente');
        setShowForm(false);
        return;
      }
      setShowForm(false);
    } catch (err) {
      pushToast('danger', err.message || 'No se pudo registrar');
    }
  };

  return (
    <div className="container-fluid">
      <div className="mb-2">
        <h2 className="mb-2 text-center">Seguimiento de Ubicaciones</h2>
      </div>
      
      {/* Toasts flotantes */}
      <div className="gp-toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast align-items-center text-bg-${t.type} show`} role="alert" aria-live="assertive" aria-atomic="true">
            <div className="d-flex">
              <div className="toast-body">{t.msg}</div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))} aria-label="Close"></button>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-3 d-flex gap-2 justify-content-end">
        <button className="btn btn-primary" title="Registrar" onClick={openCreate}><i className="bi bi-plus-lg" /></button>
        <button className="btn btn-outline-secondary" title="Actualizar" onClick={reload}><i className="bi bi-arrow-clockwise" /></button>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 110 }}>Acciones</th>
                <th>Usuario</th>
                <th>Latitud</th>
                <th>Longitud</th>
                <th>Fecha de Ubicación</th>
                <th>Exactitud (m)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">Sin registros</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button className="btn btn-outline-danger" title="Eliminar" onClick={() => setConfirmTarget({ id: r.id, userName: r.userName })}><i className="bi bi-trash" /></button>
                      <button className="btn btn-outline-primary" title="Modificar" onClick={() => openEdit(r)}><i className="bi bi-pencil" /></button>
                    </div>
                  </td>
                  <td>{r.userName || r.userId}</td>
                  <td>{r.latitude}</td>
                  <td>{r.longitude}</td>
                  <td>{r.locationDate ? new Date(r.locationDate).toLocaleString() : ''}</td>
                  <td>{r.accuracy}m</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal crear/editar */}
      <div className={`modal fade ${showForm ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal={showForm}>
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <form onSubmit={saveForm}>
              <div className="modal-header">
                <h5 className="modal-title">{editingId == null ? 'Registrar Seguimiento de Ubicación' : 'Modificar Seguimiento de Ubicación'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowForm(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Usuario</label>
                  {usersLoading ? (
                    <div className="form-control-plaintext">Cargando...</div>
                  ) : (
                    <select className="form-select" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} required>
                      <option value="">Seleccionar usuario...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>{user.nombres} {user.apellidos}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Latitud</label>
                  <input type="number" step="any" className="form-control" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Longitud</label>
                  <input type="number" step="any" className="form-control" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Fecha de Ubicación</label>
                  <input type="datetime-local" className="form-control" value={form.locationDate} onChange={(e) => setForm({ ...form, locationDate: e.target.value })} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Exactitud (metros)</label>
                  <input type="number" step="any" className="form-control" value={form.accuracy} onChange={(e) => setForm({ ...form, accuracy: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showForm && <div className="modal-backdrop fade show" onClick={() => setShowForm(false)} />}

      {/* Confirmación de eliminación */}
      <div className={`modal fade ${confirmTarget ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal={!!confirmTarget}>
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirmación</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmTarget(null)} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              {`¿Está seguro que desea eliminar el seguimiento de ubicación para: ${confirmTarget?.userName ?? ''}?`}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmTarget(null)}>No</button>
              <button type="button" className="btn btn-danger" onClick={async () => { const id = confirmTarget.id; setConfirmTarget(null); await removeRow(id); }}>Sí, eliminar</button>
            </div>
          </div>
        </div>
      </div>
      {confirmTarget && <div className="modal-backdrop fade show" onClick={() => setConfirmTarget(null)} />}
    </div>
  );
}